/**
 * Type Guards for Content Blocks
 *
 * Type guard functions to identify different content block types.
 * Used for safe type narrowing in TypeScript.
 *
 * @module lib/storage/type-guards
 */

import type { Base64ContentBlock, StorageAudioBlock, StorageImageBlock, StoragePdfBlock } from "@/features/chat/types/messages";
import type {
  AnthropicImageContent,
  AnthropicDocumentContent,
} from "@/features/chat/types/anthropic";

// =============================================================================
// ANTHROPIC FORMAT TYPE GUARDS
// =============================================================================

/**
 * Type guard for Anthropic image content format
 */
export function isAnthropicImageContent(
  block: unknown
): block is AnthropicImageContent {
  if (typeof block !== "object" || block === null) return false;
  const b = block as Record<string, unknown>;
  return (
    b.type === "image" &&
    b.source !== null &&
    typeof b.source === "object" &&
    (b.source as Record<string, unknown>).type === "base64" &&
    typeof (b.source as Record<string, unknown>).media_type === "string" &&
    typeof (b.source as Record<string, unknown>).data === "string"
  );
}

/**
 * Type guard for Anthropic document content format
 */
export function isAnthropicDocumentContent(
  block: unknown
): block is AnthropicDocumentContent {
  if (typeof block !== "object" || block === null) return false;
  const b = block as Record<string, unknown>;
  return (
    b.type === "document" &&
    b.source !== null &&
    typeof b.source === "object" &&
    (b.source as Record<string, unknown>).type === "base64" &&
    typeof (b.source as Record<string, unknown>).media_type === "string" &&
    typeof (b.source as Record<string, unknown>).data === "string"
  );
}

// =============================================================================
// INTERNAL FORMAT TYPE GUARDS
// =============================================================================

/**
 * Type guard for internal Base64ContentBlock format (used in UI)
 */
export function isBase64ContentBlock(
  block: unknown
): block is Base64ContentBlock {
  if (typeof block !== "object" || block === null) return false;
  const b = block as Record<string, unknown>;
  return (
    (b.type === "image" ||
      b.type === "file" ||
      b.type === "document" ||
      b.type === "audio" ||
      b.type === "video") &&
    b.source_type === "base64" &&
    typeof b.mime_type === "string" &&
    typeof b.data === "string"
  );
}

/**
 * Type guard for storage-referenced audio blocks.
 * Expects frontend format: source_type="storage" + mime_type at root + relative_gcs_path
 */
export function isStorageAudioBlock(
  block: unknown
): block is StorageAudioBlock {
  if (typeof block !== "object" || block === null) return false;
  const b = block as Record<string, unknown>;

  return (
    b.type === "audio" &&
    b.source_type === "storage" &&
    typeof b.relative_gcs_path === "string" &&
    typeof b.mime_type === "string"
  );
}

/**
 * Type guard for storage PDF block
 */
export function isStoragePdfBlock(block: unknown): block is StoragePdfBlock {
  if (typeof block !== "object" || block === null) return false;
  const b = block as Record<string, unknown>;
  return (
    b.type === "file" &&
    b.source_type === "storage" &&
    b.mime_type === "application/pdf" &&
    typeof b.fileName === "string"
  );
}

/**
 * Type guard for storage image block.
 * Expects frontend format: source_type="storage" + fileName + relative_gcs_path + mime_type at root
 */
export function isStorageImageBlock(
  block: unknown
): block is StorageImageBlock {
  if (typeof block !== "object" || block === null) return false;
  const b = block as Record<string, unknown>;

  return (
    b.type === "image" &&
    b.source_type === "storage" &&
    typeof b.fileName === "string" &&
    typeof b.relative_gcs_path === "string" &&
    typeof b.mime_type === "string"
  );
}
